import React, { useEffect, useState, useRef } from 'react';
import { Spin, Typography, Row, Col, Result, Button, message } from 'antd';
import { useAuth } from '../utils/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const Callback = () => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [error, setError] = useState(null);
  const [waitTime, setWaitTime] = useState(0);
  const [redirecting, setRedirecting] = useState(false);
  const timersRef = useRef({ timer: null, timeoutTimer: null });
  
  // 防止多次执行
  const checkStartedRef = useRef(false);

  useEffect(() => {
    // 如果已经开始检查，则不再重复初始化
    if (checkStartedRef.current) {
      console.log('Callback组件: 已经开始检查，跳过重复初始化');
      return;
    }
    
    checkStartedRef.current = true;
    console.log('Callback组件: 首次初始化');

    // 从URL中获取授权码和状态参数
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    
    console.log('Callback组件: 当前路径和参数:', { 
      pathname: location.pathname,
      fullUrl: window.location.href,
      hasCode: !!code,
      hasState: !!state
    });
    
    // 如果缺少必要参数，显示错误
    if (!code || !state) {
      console.error('Callback组件: 缺少必要的认证参数');
      setError('缺少必要的认证参数');
      return;
    }

    console.log('Callback组件: 检测到认证参数 code=' + code.substring(0, 5) + '... state=' + state);
    console.log('Callback组件: localStorage当前状态:', {
      token: !!localStorage.getItem('token'),
      user: !!localStorage.getItem('user'),
      redirectPath: localStorage.getItem('redirectPath') || '/'
    });
    
    // 设置最长等待时间（20秒）
    const maxWaitTime = 20;
    
    // 监视认证状态的计时器
    const checkAuthCompletion = () => {
      // 如果已经在重定向，跳过检查
      if (redirecting) return;
      
      // 如果认证加载结束
      if (!authLoading) {
        console.log('Callback组件: 认证状态检查 - 加载完成, 认证状态:', isAuthenticated);
        
        if (isAuthenticated) {
          // 认证成功，准备重定向
          clearInterval(timersRef.current.timer);
          clearTimeout(timersRef.current.timeoutTimer);
          console.log('Callback组件: 认证成功，准备重定向');
          
          // 设置重定向状态
          setRedirecting(true);
          
          // 认证成功消息和重定向
          message.success('登录成功，正在跳转...', 1, () => {
            const redirectPath = localStorage.getItem('redirectPath') || '/';
            console.log('Callback组件: 重定向到', redirectPath);
            localStorage.removeItem('redirectPath');
            navigate(redirectPath);
          });
          return;
        }
      }
      
      // 增加等待时间计数
      setWaitTime(prevTime => {
        const newTime = prevTime + 1;
        // 如果超过最大等待时间但仍未认证，显示错误
        if (newTime >= maxWaitTime && !isAuthenticated && !authLoading) {
          clearInterval(timersRef.current.timer);
          console.error('Callback组件: 认证处理超时');
          setError('认证处理超时，请重试');
          return maxWaitTime;
        }
        return newTime;
      });
    };
    
    // 如果超时后仍未完成认证，显示错误
    timersRef.current.timeoutTimer = setTimeout(() => {
      if (!isAuthenticated && !redirecting) {
        clearInterval(timersRef.current.timer);
        setError('认证处理超时，请重试');
      }
    }, maxWaitTime * 1000);
    
    // 每秒检查一次认证状态
    timersRef.current.timer = setInterval(checkAuthCompletion, 1000);
    
    // 清理函数
    return () => {
      console.log('Callback组件: 清理计时器');
      clearInterval(timersRef.current.timer);
      clearTimeout(timersRef.current.timeoutTimer);
    };
  }, []);  // 空依赖数组，只执行一次

  // 监听认证状态变化
  useEffect(() => {
    if (!checkStartedRef.current) return;
    
    if (isAuthenticated && !redirecting) {
      console.log('Callback组件: 检测到认证状态变为已认证');
      clearInterval(timersRef.current.timer);
      clearTimeout(timersRef.current.timeoutTimer);
      
      setRedirecting(true);
      message.success('登录成功，正在跳转...', 1, () => {
        const redirectPath = localStorage.getItem('redirectPath') || '/';
        localStorage.removeItem('redirectPath');
        navigate(redirectPath);
      });
    }
  }, [isAuthenticated, redirecting]);

  // 如果发生错误，显示错误信息
  if (error) {
    return (
      <Row justify="center" align="middle" style={{ minHeight: '100vh' }}>
        <Col>
          <Result
            status="error"
            title="认证失败"
            subTitle={error}
            icon={<CloseCircleOutlined />}
            extra={[
              <Button type="primary" key="retry" onClick={() => window.location.href = "/"}>
                返回首页重试
              </Button>
            ]}
          />
        </Col>
      </Row>
    );
  }
  
  // 如果认证成功且正在重定向
  if (isAuthenticated && redirecting) {
    return (
      <Row justify="center" align="middle" style={{ minHeight: '100vh' }}>
        <Col>
          <Result
            status="success"
            title="登录成功"
            subTitle="正在跳转到系统..."
            icon={<CheckCircleOutlined />}
          />
        </Col>
      </Row>
    );
  }

  // 正在处理时显示加载中
  return (
    <Row justify="center" align="middle" style={{ minHeight: '100vh' }}>
      <Col>
        <div style={{ textAlign: 'center' }}>
          <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 36 }} spin />} />
          <Title level={3} style={{ marginTop: '24px' }}>
            正在处理Casdoor登录...
          </Title>
          <Text type="secondary">
            请稍候，系统正在验证您的身份 ({waitTime}/{20}秒)
          </Text>
        </div>
      </Col>
    </Row>
  );
};

export default Callback; 